import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { 
  Settings, ChevronLeft, User, Mail, Phone, Building,
  Globe, Moon, Bell, Loader2, CheckCircle, AlertCircle,
  LogOut, CreditCard, Crown, Trash2
} from 'lucide-react';

const LANGUAGES = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' }
];

const PLANS = {
  free: { name: 'Gratuit', price: '0€', trips: '3/mois', color: 'slate' },
  pro: { name: 'Pro', price: '4,99€/mois', trips: 'Illimités', color: 'blue' },
  pro_plus: { name: 'Pro+', price: '14,99€/mois', trips: 'Illimités + Compta', color: 'violet' },
  fleet: { name: 'Fleet', price: '99€/mois', trips: 'Flotte complète', color: 'amber' }
};

const SettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [mounted, setMounted] = useState(false);
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    company_name: '',
    phone: '',
    language: 'fr'
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    setMounted(true);
    fetchUser();
  }, []);

  const fetchUser = async () => {
    const token = localStorage.getItem('optitrajet_token');
    if (!token) {
      navigate('/optitrajet/login');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/optitrajet/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        setFormData({
          full_name: data.full_name || '',
          company_name: data.company_name || '',
          phone: data.phone || '',
          language: data.language || 'fr'
        });
      }
    } catch (err) {
      console.error('Fetch user error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    const token = localStorage.getItem('optitrajet_token');

    try {
      const res = await fetch(`${API_URL}/api/optitrajet/auth/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!res.ok) {
        throw new Error('Erreur lors de la sauvegarde');
      }

      const data = await res.json();
      setUser(data);
      localStorage.setItem('optitrajet_user', JSON.stringify(data));
      setSuccess('Paramètres enregistrés');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('optitrajet_token');
    localStorage.removeItem('optitrajet_user');
    navigate('/optitrajet/login');
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('⚠️ Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible.')) return;
    if (!window.confirm('Cette action supprimera TOUTES vos données (trajets, dépenses, véhicules). Continuer ?')) return;

    const token = localStorage.getItem('optitrajet_token');

    try {
      await fetch(`${API_URL}/api/optitrajet/auth/me`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      handleLogout();
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
  };

  const plan = user ? PLANS[user.subscription_plan] || PLANS.free : PLANS.free;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8FAFC' }}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 lg:pb-8" style={{ background: '#F8FAFC', fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center">
          <button 
            onClick={() => navigate('/optitrajet/dashboard')}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-slate-600" />
          </button>
          <h1 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-xl font-bold text-slate-800 ml-2">
            Paramètres
          </h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Success/Error messages */}
        {success && (
          <div className="mb-4 p-4 rounded-xl bg-green-50 border border-green-100 flex items-center gap-3 animate-in slide-in-from-top duration-300">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <p className="text-green-700">{success}</p>
          </div>
        )}
        {error && (
          <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-100 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Subscription Plan */}
        <Card className={`mb-6 border-0 shadow-lg overflow-hidden transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <CardContent className="p-0">
            <div className={`p-6 bg-gradient-to-r from-${plan.color}-500 to-${plan.color}-600 text-white`}
              style={{ 
                background: plan.color === 'slate' 
                  ? 'linear-gradient(135deg, #64748b, #475569)' 
                  : plan.color === 'blue'
                  ? 'linear-gradient(135deg, #3b82f6, #2563eb)'
                  : plan.color === 'violet'
                  ? 'linear-gradient(135deg, #8b5cf6, #7c3aed)'
                  : 'linear-gradient(135deg, #f59e0b, #d97706)'
              }}
            >
              <div className="flex items-center gap-3 mb-2">
                <Crown className="h-6 w-6" />
                <span className="font-semibold">Plan {plan.name}</span>
              </div>
              <p className="text-2xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {plan.price}
              </p>
              <p className="text-sm opacity-90">{plan.trips} trajets</p>
            </div>
            <div className="p-4 bg-white">
              <Button variant="outline" className="w-full rounded-xl">
                <CreditCard className="h-4 w-4 mr-2" />
                Gérer l'abonnement
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Profile Settings */}
        <Card className={`mb-6 border-0 shadow-lg transition-all duration-500 delay-100 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <CardContent className="p-6">
            <h3 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-lg font-semibold text-slate-800 mb-4">
              Profil
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Nom complet</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="pl-12 h-12 rounded-xl border-slate-200"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    value={user?.email || ''}
                    disabled
                    className="pl-12 h-12 rounded-xl border-slate-200 bg-slate-50 text-slate-500"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Entreprise (optionnel)</label>
                <div className="relative">
                  <Building className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="Nom de l'entreprise"
                    className="pl-12 h-12 rounded-xl border-slate-200"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Téléphone</label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+33 6 12 34 56 78"
                    className="pl-12 h-12 rounded-xl border-slate-200"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Langue</label>
                <div className="relative">
                  <Globe className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <select
                    value={formData.language}
                    onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                    className="w-full pl-12 h-12 rounded-xl border border-slate-200 bg-white"
                  >
                    {LANGUAGES.map(lang => (
                      <option key={lang.code} value={lang.code}>
                        {lang.flag} {lang.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <Button 
                onClick={handleSave}
                disabled={saving}
                className="w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-700"
              >
                {saving ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Logout & Delete */}
        <Card className={`border-0 shadow-lg transition-all duration-500 delay-200 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <CardContent className="p-6 space-y-3">
            <Button 
              onClick={handleLogout}
              variant="outline"
              className="w-full h-12 rounded-xl border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              <LogOut className="h-5 w-5 mr-2" />
              Se déconnecter
            </Button>
            
            <Button 
              onClick={handleDeleteAccount}
              variant="outline"
              className="w-full h-12 rounded-xl border-red-200 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-5 w-5 mr-2" />
              Supprimer mon compte
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default SettingsPage;
